import React from 'react';
import Joyride from 'react-joyride';
import { useTour } from './../contexts/TourContext';
import { Button, Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';

function CustomTooltip({ step, backProps, primaryProps, tooltipProps, skipProps, ...rest }) {
    return (
        <Paper elevation={4} sx={{ p: 2, maxWidth: 360, position: 'relative' }} {...tooltipProps}>
            <Typography variant="subtitle1">{step.title}</Typography>
            <Typography variant="body2">{step.content}</Typography>
            <Box display="flex" justifyContent={step?.data?.isLast ? 'flex-end' : 'space-between'} mt={2}>
                {!step?.data?.isLast && (
                    <Box display="flex" justifyContent="flex-start">
                        <Button {...skipProps} sx={{ minWidth: 'auto', padding: '2px 8px', fontSize: '0.75rem' }} size="small">
                            Skip
                        </Button>
                    </Box>
                )}
                <Box display="flex" justifyContent="flex-end">
                    {rest.index > 0 && (
                        <Button size="small" {...backProps}>
                            Back
                        </Button>
                    )}
                    <Button size="small" variant="contained" {...primaryProps}>
                        {step?.data?.isLast ? 'Finish' : 'Next'}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
}

CustomTooltip.propTypes = {
    step: PropTypes.object,
    backProps: PropTypes.object,
    primaryProps: PropTypes.object,
    tooltipProps: PropTypes.object,
    skipProps: PropTypes.object
};

const TourManager = () => {
    const { isRunning, steps, stepIndex, setStepIndex, stopTour } = useTour();

    return (
        <Joyride
            scrollToFirstStep
            scrollOffset={200} // Optional: prevent tooltip from being too close to top
            // scrollToSteps={true}
            steps={steps}
            run={isRunning}
            stepIndex={stepIndex}
            continuous
            disableOverlayClose
            styles={{ options: { zIndex: 2000 } }}
            tooltipComponent={CustomTooltip}
            disableScrollParentFix={true}
            callback={(data) => {
                const { status, index, type, action, step } = data;

                if (['finished', 'skipped'].includes(status)) {
                    stopTour();
                } else if (type === 'step:before') {
                    // Scroll to target element when entering a step
                    const target = step.target;
                    if (target && typeof target === 'string') {
                        const element = document.querySelector(target);
                        if (element) {
                            // Check if this step needs special table scrolling
                            const needsScrolling = step.data?.scrollToElement;

                            // Check if element is inside a scrollable table container
                            const tableContainer = element.closest('[class*="TableContainer"], .MuiTableContainer-root');
                            if (tableContainer && needsScrolling) {
                                // Calculate if element is out of horizontal view
                                const elementRect = element.getBoundingClientRect();
                                const containerRect = tableContainer.getBoundingClientRect();

                                const isOutOfViewLeft = elementRect.left < containerRect.left;
                                const isOutOfViewRight = elementRect.right > containerRect.right;

                                if (isOutOfViewLeft || isOutOfViewRight) {
                                    // Calculate scroll position to center the element
                                    const elementCenterX = element.offsetLeft + element.offsetWidth / 2;
                                    const containerCenterX = tableContainer.offsetWidth / 2;
                                    const scrollLeft = elementCenterX - containerCenterX;

                                    // Smooth scroll the table container horizontally
                                    tableContainer.scrollTo({
                                        left: Math.max(0, scrollLeft),
                                        behavior: 'smooth'
                                    });
                                }
                            }

                            // Always scroll vertically to ensure element is visible
                            if (step?.data?.disableScroll) return;
                            setTimeout(
                                () => {
                                    element.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'nearest',
                                        inline: needsScrolling ? 'center' : 'nearest'
                                    });
                                },
                                tableContainer && needsScrolling ? 300 : 0
                            ); // Delay if horizontal scroll happened
                        }
                    }
                } else if (type === 'step:after') {
                    if (action === 'next') {
                        if (step?.data?.actions?.onNext) {
                            step.data.actions.onNext();
                        }
                    }
                    setStepIndex(index + 1);
                }
            }}
        />
    );
};

export default TourManager;
